import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import {
  TsButtonModule,
  TsColorBubbleComponent,
  TsDividerComponent,
  TSIconModule,
  TsInputComponent,
  TsPipesModule,
} from 'tsui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgApexchartsModule } from 'ng-apexcharts';
import { FormArray, FormBuilder, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ColorPickerDirective, ColorPickerModule, ColorPickerService } from 'ngx-color-picker';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TsEmptyStateComponent } from 'tsui/lib/ts-empty-state/ts-empty-state.component';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { ChartRendererComponent } from '../visualization-renderer/chart-renderer.component';
import { SimpleOption } from 'tsui/lib/models/simple-option';
import { MatTooltipModule } from '@angular/material/tooltip';
import { debounceTime, filter, finalize, map, take } from 'rxjs/operators';
import { ChartRendererUtils } from '../../utils/chart-renderer.utils';
import { PanelFormFields } from '../../models/panel-form-fields.enum';
import {
  ChartFormFields,
  ChartPanelBuilderDialogData,
  ChartVisualizationConfig,
  ChartVisualizationConfigMeta,
} from '../../models';
import { TsPanelComponent } from 'tsui/lib/ts-panel/ts-panel.component';
import { ReportPeriodUtils } from '../../utils/report-period.utils';
import { RelatedToType } from '@shared/enums';
import { TsSkeletonComponent } from 'tsui/lib/ts-skeleton/ts-skeleton.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'chart-panel-builder-dialog',
  standalone: true,
  imports: [
    TsDialogComponent,
    TsButtonModule,
    NgApexchartsModule,
    ReactiveFormsModule,
    MatButtonToggleModule,
    AsyncPipe,
    MatCheckboxModule,
    MatFormFieldModule,
    MatSelectModule,
    ColorPickerModule,
    TsColorBubbleComponent,
    MatSlideToggleModule,
    TsEmptyStateComponent,
    TsPipesModule,
    TsSelectModule,
    TsDividerComponent,
    TsInputComponent,
    NgTemplateOutlet,
    TSIconModule,
    NgStyle,
    ChartRendererComponent,
    MatTooltipModule,
    TsPanelComponent,
    TsSkeletonComponent,
    NgClass,
  ],
  providers: [ColorPickerService],
  templateUrl: './chart-panel-builder-dialog.component.html',
  styleUrl: './chart-panel-builder-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartPanelBuilderDialogComponent implements OnInit {
  readonly data: ChartPanelBuilderDialogData = inject(DIALOG_DATA);
  readonly dialogRef = inject(DialogRef);
  readonly fb = inject(FormBuilder);
  readonly store = inject(Store);
  readonly destroyRef = inject(DestroyRef);
  readonly confirmService = inject(ConfirmationDialogService);

  readonly reportPeriodOptions = ReportPeriodUtils.buildReportPeriodOptions(['custom']);
  readonly accountColorOptions$ = this.store.select(AccountState.selectAccountColorsWithNone);
  readonly visualizationOptions: SimpleOption[] = ChartRendererUtils.visualizationOptions;
  readonly relatedToOptions = [
    { value: RelatedToType.AssetManagement, label: 'Asset Management' },
    { value: RelatedToType.Property, label: 'Property' },
  ];

  readonly panelForm: UntypedFormGroup = this.fb.group({
    [PanelFormFields.title]: [this.editChartVisualizationData?.title || '', Validators.required],
    [PanelFormFields.type]: [this.editChartVisualizationData?.meta?.type || null, Validators.required],
    [PanelFormFields.headerColor]: [this.editChartVisualizationData?.headerColor || null],
    [ChartFormFields.reportPeriod]: [this.editChartVisualizationData?.reportPeriod?.key || null, Validators.required],
  });
  readonly relatedToForm = this.fb.control(this.editChartVisualizationData?.related_to || null, Validators.required);
  readonly visualizationForm: UntypedFormGroup = this.fb.group({
    [ChartFormFields.field]: [
      this.editChartVisualizationData?.series?.map(({ fieldId }) => fieldId) || [],
      [Validators.required, Validators.minLength(1)],
    ],
    [ChartFormFields.direction]: [this.editChartVisualizationData?.meta?.direction || 'vertical', Validators.required],
    [ChartFormFields.reversed]: [this.editChartVisualizationData?.meta?.reversed || false, Validators.required],
    [ChartFormFields.stacked]: [this.editChartVisualizationData?.meta?.stacked || false, Validators.required],
  });
  readonly fieldsForm = <FormArray<any>>this.fb.array([]);

  readonly visualizationConfig = new BehaviorSubject<ChartVisualizationConfig>(null);
  readonly previewPanelDataSource = new BehaviorSubject(null);
  readonly previewChartData = combineLatest([this.visualizationConfig, this.previewPanelDataSource]);
  readonly loadingPreview = signal(false);
  readonly loadingFields = signal(false);
  readonly chartPreviewUpdateTrigger = new Subject<boolean>();

  readonly fieldOptionsLabelMap = this.fieldOptions.pipe(
    map((options) => options?.reduce((acc, option) => ({ ...acc, [option.value]: option.label }), {})),
  );

  @ViewChildren(ColorPickerDirective) colorPickers: QueryList<ColorPickerDirective>;

  get panelFormControls(): { [key: string]: any } {
    return {
      [PanelFormFields.title]: this.panelForm.get(PanelFormFields.title),
      [PanelFormFields.headerColor]: this.panelForm.get(PanelFormFields.headerColor),
      [PanelFormFields.type]: this.panelForm.get(PanelFormFields.type),
      [ChartFormFields.reportPeriod]: this.panelForm.get(ChartFormFields.reportPeriod),
    };
  }

  get visualizationFormControls(): { [key: string]: any } {
    return {
      [ChartFormFields.field]: this.visualizationForm.get(ChartFormFields.field),
      [ChartFormFields.direction]: this.visualizationForm.get(ChartFormFields.direction),
      [ChartFormFields.stacked]: this.visualizationForm.get(ChartFormFields.stacked),
      [ChartFormFields.reversed]: this.visualizationForm.get(ChartFormFields.reversed),
    };
  }

  get fieldsFormControls(): UntypedFormGroup[] {
    return this.fieldsForm.controls as UntypedFormGroup[];
  }

  get enableVisualizationSettings() {
    return this.panelForm.valid && (this.relatedToForm.valid || this.relatedToForm.disabled);
  }

  get fieldOptions() {
    return this.data.fieldOptions;
  }

  get relatedToChanged() {
    return this.data.relatedToChanged;
  }

  get editChartVisualizationData(): ChartVisualizationConfig {
    return this.data?.chartVisualizationConfig;
  }

  get chartVisualizationMeta(): ChartVisualizationConfigMeta {
    return {
      type: this.panelFormControls.type.value,
      direction: this.visualizationFormControls.direction.value,
      reversed: this.visualizationFormControls.reversed.value,
      stacked: this.visualizationFormControls.stacked.value,
    };
  }

  ngOnInit() {
    this.chartPreviewUpdateTrigger.pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateChartPreview();
    });

    if (this.editChartVisualizationData) {
      this.loadingFields.set(true);
      this.relatedToChanged.next(this.editChartVisualizationData.related_to);
      this.relatedToForm.disable({ emitEvent: false });

      this.fieldOptions
        .pipe(
          filter((options) => Array.isArray(options) && options.length > 0),
          take(1),
        )
        .subscribe(() => {
          this.editChartVisualizationData.series.forEach(({ fieldId, type, color, displayLabels }) => {
            this.fieldsForm.push(this.buildFieldsForm(fieldId, type, color, displayLabels));
          });
          this.chartPreviewUpdateTrigger.next(false);
          this.loadingFields.set(false);
        });
    }

    this.panelForm.valueChanges.pipe(debounceTime(800), takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.chartPreviewUpdateTrigger.next(false);
    });

    this.relatedToForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: RelatedToType) => {
      this.resetFieldsAndPreview();
      this.data.relatedToChanged.next(value);
    });

    this.visualizationForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((visualizationField) => {
      const fieldIds = visualizationField.field;

      if (fieldIds.length > 0) {
        this.updateFieldsForm(fieldIds);
        this.chartPreviewUpdateTrigger.next(true);
      } else {
        this.resetFieldsAndPreview();
      }
    });

    this.fieldsForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.chartPreviewUpdateTrigger.next(true);
    });
  }

  onFieldColorSelected(color: string, previousColor: string, fieldFormIndex: number) {
    if (color.toLowerCase() === previousColor.toLowerCase()) return;

    this.fieldsForm.at(fieldFormIndex).patchValue({ color }, { emitModelToViewChange: true });
  }

  openColorPicker(i: number, color?: string) {
    this.colorPickers.get(i).colorPicker = color ?? '#FFFFFF';
    this.colorPickers.get(i).openDialog();
  }

  createPanel() {
    this.fieldOptions.next(null);
    this.dialogRef.close(this.visualizationConfig.value);
  }

  closeDialog() {
    if (this.panelForm.pristine && this.visualizationForm.pristine && this.fieldsForm.pristine) {
      this.dialogRef.close();
    } else {
      this.confirmService
        .openConfirmationDialog(ConfirmationDialogService.unsavedChangesCommonLayoutConfig())
        .pipe(
          filter((result) => result),
          take(1),
        )
        .subscribe(() => {
          this.dialogRef.close();
          this.fieldOptions.next(null);
        });
    }
  }

  private resetFieldsAndPreview() {
    this.fieldsForm.clear();
    this.visualizationFormControls[ChartFormFields.field].reset([], { emitEvent: false });
    this.visualizationConfig.next(null);
  }

  private convertToChartVisualization(fieldIds: number[]): ChartVisualizationConfig {
    const title = this.panelFormControls.title.value;
    const headerColor = this.panelFormControls.headerColor.value;
    const series = this.chartVisualizationSeriesFactory(fieldIds);
    const meta = { ...this.chartVisualizationMeta };
    const reportPeriod = ReportPeriodUtils.buildReportPeriod({ key: this.panelFormControls.reportPeriod.value });
    const related_to = this.relatedToForm.value;

    return {
      title,
      headerColor,
      series,
      meta,
      reportPeriod,
      related_to,
    };
  }

  private updateChartPreview(visualizationOnly = false) {
    const fieldIds = this.visualizationFormControls.field.value;

    if (fieldIds.length > 0) {
      const chartVisualizationConfig = this.convertToChartVisualization(fieldIds);

      if (visualizationOnly) {
        this.visualizationConfig.next(chartVisualizationConfig);
        return;
      }

      this.loadingPreview.set(true);

      this.data
        .dataSource(chartVisualizationConfig)
        .pipe(
          take(1),
          finalize(() => this.loadingPreview.set(false)),
        )
        .subscribe((previewPanelDataSource) => {
          this.visualizationConfig.next(chartVisualizationConfig);
          this.previewPanelDataSource.next(previewPanelDataSource);
        });
    }
  }

  private buildFieldsForm(
    id: number | string,
    type?: string,
    color?: string,
    displayLabels?: boolean,
  ): UntypedFormGroup {
    return this.fb.group({
      fieldId: [id, Validators.required],
      type: [type || 'column', Validators.required],
      color: [color || ChartRendererUtils.generateRandomColor()],
      displayLabels: [displayLabels || false],
    });
  }

  private updateFieldsForm(fieldIds: number[]) {
    if (fieldIds.length < this.fieldsForm.controls.length) {
      this.removeFieldFromForm(fieldIds);
    } else {
      this.addFieldToForm(fieldIds);
    }
  }

  private addFieldToForm(fieldIds: number[]) {
    const fieldIdsNotInForm = fieldIds.filter((fieldId) => {
      return !this.fieldsForm.value.some((field) => field.fieldId === fieldId);
    });

    fieldIdsNotInForm.forEach((fieldId) => {
      this.fieldsForm.push(this.buildFieldsForm(fieldId));
    });
  }

  private removeFieldFromForm(fieldIds: number[]) {
    const indexOfFieldsToRemove = this.fieldsForm.controls.reduce((acc, control, index) => {
      if (!fieldIds.includes(control.get('fieldId').value)) {
        acc.push(index);
      }
      return acc;
    }, []);

    indexOfFieldsToRemove.forEach((index) => {
      this.fieldsForm.removeAt(index);
    });
  }

  private chartVisualizationSeriesFactory(fieldIds: (number | string)[]) {
    return fieldIds.map((fieldId) => {
      const chartField = this.fieldsFormControls.find((field) => field.get('fieldId').value === fieldId)?.value;
      const name = this.fieldOptions.value.find((option) => option.value === fieldId)?.label;
      const format = this.fieldOptions.value.find((option) => option.value === fieldId)?.format || 'number';

      return {
        fieldId,
        name,
        ...(this.panelFormControls.type.value === 'mixed' ? { type: chartField?.type || 'column' } : {}),
        color: chartField?.color || ChartRendererUtils.generateRandomColor(),
        displayLabels: chartField?.displayLabels || false,
        format,
      };
    });
  }
}
